/**
 * MyClips V3 — Faz L-5: Empty-state line-art illustrations
 *
 * Component spec ajanı kararı: line-art (1.5px stroke, rounded caps) +
 * 1 brand-gradient "spot accent" per illustration. Flat/isometric
 * premium editorial hissi vermez, line-art verir (Apple Music / Linear
 * tarzı).
 *
 * Üç varyant (minimal iskele — her biri ~80 SVG node):
 *   feed      → kamera + ışık huzmesi + sparkle spot
 *   following → kalp + takipçi silueti + sparkle spot
 *   saved     → bookmark + çiçek motif + sparkle spot
 *   search    → büyüteç + soru işareti + sparkle spot
 *
 * Tüm stroke'lar currentColor (tema-aware — light'ta slate, dark'ta
 * light slate). Gradient spot SVG <defs> içinde linearGradient ile
 * (brand blue→purple→magenta).
 *
 * Props:
 *   variant — "feed" | "following" | "saved" | "search" | "generic"
 *   size    — px cinsinden toplam genişlik (default 160)
 *
 * Tasarım: animate değil (statik — "empty" state'i kararlı hissettirir,
 * kalabalıklaşmaz). Motion spec'te "illustration static" kuralı.
 */

import React from "react";

const STROKE = 1.5;

/**
 * Her glyph'in aldığı iki prop — gövdeden türetildi: `size` px genişlik
 * (`(size * 120) / 160` ile çarpılıyor → number), `gradId` SVG `<linearGradient>`
 * id'si (`url(#${gradId})` içinde string olarak gömülüyor).
 */
type GlyphProps = { size: number; gradId: string };

/* Her glyph 160×120 viewBox'ta çizilir — içinde brand-gradient spot
   (8-uçlu sparkle veya dot) vardır. Gradient id'ler çakışmasın diye
   React.useId pattern'i; statik kullanımda sabit id yeter. */

function Feed({ size, gradId }: GlyphProps) {
  return (
    <svg width={size} height={(size * 120) / 160} viewBox="0 0 160 120" fill="none" aria-hidden="true">
      {/* Kamera gövde */}
      <rect x="34" y="46" width="80" height="54" rx="10" stroke="currentColor" strokeWidth={STROKE} strokeLinejoin="round" />
      {/* Lens */}
      <circle cx="74" cy="73" r="16" stroke="currentColor" strokeWidth={STROKE} />
      <circle cx="74" cy="73" r="7"  stroke="currentColor" strokeWidth={STROKE} />
      {/* Üst tepe (flaş) */}
      <path d="M56 46 L58 40 L90 40 L92 46" stroke="currentColor" strokeWidth={STROKE} strokeLinecap="round" strokeLinejoin="round" />
      {/* Işık huzmesi */}
      <path d="M110 52 L130 40 M112 60 L134 56 M114 68 L138 72" stroke="currentColor" strokeWidth={STROKE} strokeLinecap="round" opacity="0.55" />
      {/* Brand sparkle spot */}
      <g transform="translate(130 24)">
        <path d="M0 -8 L2 -2 L8 0 L2 2 L0 8 L-2 2 L-8 0 L-2 -2 Z" fill={`url(#${gradId})`} />
      </g>
      <g transform="translate(26 30)">
        <circle cx="0" cy="0" r="2.5" fill={`url(#${gradId})`} />
      </g>
    </svg>
  );
}

function Following({ size, gradId }: GlyphProps) {
  return (
    <svg width={size} height={(size * 120) / 160} viewBox="0 0 160 120" fill="none" aria-hidden="true">
      {/* Avatar silüet */}
      <circle cx="58" cy="52" r="14" stroke="currentColor" strokeWidth={STROKE} />
      <path d="M36 94 Q36 74 58 74 Q80 74 80 94" stroke="currentColor" strokeWidth={STROKE} strokeLinejoin="round" />
      {/* Kalp */}
      <path d="M110 68 C110 62 105 58 100 60 C95 58 90 62 90 68 C90 76 100 86 100 86 C100 86 110 76 110 68 Z"
            stroke="currentColor" strokeWidth={STROKE} strokeLinejoin="round" strokeLinecap="round" />
      {/* Bağlantı çizgisi */}
      <path d="M74 52 L92 62" stroke="currentColor" strokeWidth={STROKE} strokeLinecap="round" opacity="0.4" strokeDasharray="3 3" />
      {/* Brand sparkle */}
      <g transform="translate(124 36)">
        <path d="M0 -7 L2 -2 L7 0 L2 2 L0 7 L-2 2 L-7 0 L-2 -2 Z" fill={`url(#${gradId})`} />
      </g>
      <g transform="translate(100 28)">
        <circle cx="0" cy="0" r="2" fill={`url(#${gradId})`} />
      </g>
    </svg>
  );
}

function Saved({ size, gradId }: GlyphProps) {
  return (
    <svg width={size} height={(size * 120) / 160} viewBox="0 0 160 120" fill="none" aria-hidden="true">
      {/* Bookmark */}
      <path d="M60 30 H100 V96 L80 82 L60 96 Z"
            stroke="currentColor" strokeWidth={STROKE} strokeLinejoin="round" />
      {/* Çiçek/yıldız motif sağ-üst */}
      <circle cx="120" cy="48" r="3" stroke="currentColor" strokeWidth={STROKE} />
      <path d="M120 38 L120 44 M120 52 L120 58 M110 48 L116 48 M124 48 L130 48" stroke="currentColor" strokeWidth={STROKE} strokeLinecap="round" />
      {/* Alt yaprak */}
      <path d="M38 86 Q42 78 50 80" stroke="currentColor" strokeWidth={STROKE} strokeLinecap="round" opacity="0.55" />
      {/* Brand sparkle */}
      <g transform="translate(30 38)">
        <path d="M0 -6 L1.5 -1.5 L6 0 L1.5 1.5 L0 6 L-1.5 1.5 L-6 0 L-1.5 -1.5 Z" fill={`url(#${gradId})`} />
      </g>
    </svg>
  );
}

function Search({ size, gradId }: GlyphProps) {
  return (
    <svg width={size} height={(size * 120) / 160} viewBox="0 0 160 120" fill="none" aria-hidden="true">
      {/* Büyüteç */}
      <circle cx="70" cy="56" r="28" stroke="currentColor" strokeWidth={STROKE} />
      <path d="M90 76 L108 94" stroke="currentColor" strokeWidth={STROKE} strokeLinecap="round" />
      {/* Soru işareti içi büyüteç */}
      <path d="M62 50 Q62 44 70 44 Q78 44 78 50 Q78 54 74 56 Q70 58 70 62 M70 68 L70 68.5" stroke="currentColor" strokeWidth={STROKE} strokeLinecap="round" strokeLinejoin="round" opacity="0.85" />
      {/* Brand sparkle */}
      <g transform="translate(128 30)">
        <path d="M0 -8 L2 -2 L8 0 L2 2 L0 8 L-2 2 L-8 0 L-2 -2 Z" fill={`url(#${gradId})`} />
      </g>
      <g transform="translate(32 92)">
        <circle cx="0" cy="0" r="2" fill={`url(#${gradId})`} />
      </g>
    </svg>
  );
}

function Generic({ size, gradId }: GlyphProps) {
  return (
    <svg width={size} height={(size * 120) / 160} viewBox="0 0 160 120" fill="none" aria-hidden="true">
      {/* Boş kart iskeletı */}
      <rect x="44" y="24" width="72" height="84" rx="8" stroke="currentColor" strokeWidth={STROKE} strokeLinejoin="round" />
      <line x1="56" y1="44" x2="104" y2="44" stroke="currentColor" strokeWidth={STROKE} strokeLinecap="round" opacity="0.5" />
      <line x1="56" y1="56" x2="90"  y2="56" stroke="currentColor" strokeWidth={STROKE} strokeLinecap="round" opacity="0.4" />
      <line x1="56" y1="68" x2="96"  y2="68" stroke="currentColor" strokeWidth={STROKE} strokeLinecap="round" opacity="0.3" />
      {/* Brand sparkle */}
      <g transform="translate(30 34)">
        <path d="M0 -7 L2 -2 L7 0 L2 2 L0 7 L-2 2 L-7 0 L-2 -2 Z" fill={`url(#${gradId})`} />
      </g>
    </svg>
  );
}

const VARIANTS = {
  feed: Feed,
  following: Following,
  saved: Saved,
  search: Search,
  generic: Generic,
};

let _idCounter = 0;
function genId() {
  _idCounter += 1;
  return `mlv3-empty-grad-${_idCounter}`;
}

type EmptyIllustrationProps = {
  variant?: string;
  size?: number;
  className?: string;
  style?: React.CSSProperties;
};

export function EmptyIllustration({ variant = "generic", size = 160, className = "", style }: EmptyIllustrationProps) {
  // `variant` prop'u serbest `string` (çağıranlar değişken geçiyor) — bilinmeyen
  // anahtar `undefined` döner ve `|| Generic` yedeğine düşer; davranış aynen korunur.
  const Glyph = (VARIANTS as Record<string, (props: GlyphProps) => React.ReactElement>)[variant] || Generic;
  // Gradient id'si render başına unique — çoklu instance çakışmaz
  const gradId = React.useMemo(genId, []);
  return (
    <div
      className={`mlv3-empty-illust ${className}`}
      style={{
        color: "var(--mlv3-fg-subtle)",
        ...style,
      }}
      aria-hidden="true"
    >
      <svg width="0" height="0" style={{ position: "absolute" }}>
        <defs>
          <linearGradient id={gradId} x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%"   stopColor="#2952FF" />
            <stop offset="50%"  stopColor="#5B2DD8" />
            <stop offset="100%" stopColor="#B84FF0" />
          </linearGradient>
        </defs>
      </svg>
      <Glyph size={size} gradId={gradId} />
    </div>
  );
}

export default EmptyIllustration;
